/**
 * Simulation, telemetry, analytics and optimizer schemas.
 */

import { z } from 'zod';
import {
  AuditFieldsSchema,
  BodyIdSchema,
  GravityFidelitySchema,
  IdSchema,
  OrbitalElementsSchema,
  TimestampSchema,
  Vec3Schema,
} from './primitives.js';
import { AscentProgramSchema, WeatherSchema } from './mission.js';

export const SimulationSettingsSchema = z.object({
  body: BodyIdSchema.default('earth'),
  gravity: GravityFidelitySchema.default('j2'),
  dragEnabled: z.boolean().default(true),
  windEnabled: z.boolean().default(true),
  /** Force model used after powered ascent. */
  coastModel: z.enum(['kepler', 'perturbed']).default('perturbed'),
  thirdBodyGravityEnabled: z.boolean().default(true),
  solarRadiationPressureEnabled: z.boolean().default(true),
  coastDragEnabled: z.boolean().default(true),
  spacecraftReflectivityCoefficient: z.number().min(0).max(2.5).default(1.3),
  /** Simulation horizon, seconds. */
  maxDurationSeconds: z.number().min(1).max(30 * 86400).default(7200),
  /** Telemetry sample interval, seconds. */
  sampleIntervalSeconds: z.number().min(0.01).max(600).default(1),
  /** Fixed step used during powered and atmospheric flight, seconds. */
  poweredStepSeconds: z.number().min(0.001).max(1).default(0.1),
  /** Maximum step during vacuum coast, seconds. */
  coastMaxStepSeconds: z.number().min(0.1).max(600).default(10),
});
export type SimulationSettingsDto = z.infer<typeof SimulationSettingsSchema>;

/**
 * Fully-populated default settings.
 *
 * Zod 4 requires `.default()` to be given a value of the schema's *output* type,
 * so `.default({})` will not type-check even though every field has its own
 * default. Parsing an empty object once resolves them all into a concrete value
 * that can then be reused as the default.
 */
export const DEFAULT_SIMULATION_SETTINGS: SimulationSettingsDto =
  SimulationSettingsSchema.parse({});

/**
 * A simulation is fully described by this request, and running it twice yields
 * bit-identical output. That is what lets a recording store the request rather
 * than several megabytes of frames.
 */
export const SimulationRequestSchema = z.object({
  missionId: IdSchema.optional(),
  rocketId: IdSchema.optional(),
  /** Inline definitions, for a run that is not backed by saved records. */
  inline: z
    .object({
      rocket: z.unknown(),
      launchSite: z.object({
        latitudeDeg: z.number(),
        longitudeDeg: z.number(),
        elevationMeters: z.number(),
      }),
      launchAt: TimestampSchema,
      ascentProgram: AscentProgramSchema,
      weather: WeatherSchema.optional(),
    })
    .optional(),
  settings: SimulationSettingsSchema.default(DEFAULT_SIMULATION_SETTINGS),
});
export type SimulationRequest = z.infer<typeof SimulationRequestSchema>;

export const FlightPhaseSchema = z.enum([
  'pre-launch',
  'vertical-rise',
  'pitch-over',
  'gravity-turn',
  'coast-to-apoapsis',
  'circularisation',
  'orbit',
  'parking-orbit',
  'departure',
  'trans-lunar',
  'interplanetary-coast',
  'arrival',
  'destination-orbit',
  'maneuver',
  'reentry',
  'landed',
]);
export type FlightPhaseDto = z.infer<typeof FlightPhaseSchema>;

export const FlightEventTypeSchema = z.enum([
  'liftoff',
  'tower-clear',
  'max-q',
  'booster-burnout',
  'booster-separation',
  'stage-burnout',
  'stage-separation',
  'stage-ignition',
  'fairing-jettison',
  'karman-crossing',
  'meco',
  'apoapsis',
  'periapsis',
  'orbit-insertion',
  'parking-orbit',
  'departure-injection',
  'sphere-of-influence-exit',
  'midcourse',
  'sphere-of-influence-entry',
  'destination-encounter',
  'capture-burn',
  'maneuver-burn',
  'mission-complete',
  'transfer-infeasible',
  'atmospheric-entry',
  'ground-impact',
  'propellant-exhausted',
  'timeout',
]);
export type FlightEventTypeDto = z.infer<typeof FlightEventTypeSchema>;

export const FlightEventSchema = z.object({
  timeSeconds: z.number(),
  epoch: TimestampSchema,
  type: FlightEventTypeSchema,
  label: z.string(),
  altitudeMeters: z.number(),
  speedMetersPerSecond: z.number(),
  detail: z.string().optional(),
  centralBodyId: BodyIdSchema.optional(),
});
export type FlightEventDto = z.infer<typeof FlightEventSchema>;

/**
 * One telemetry frame.
 *
 * Sent over the wire in a columnar form (see `TelemetrySeriesSchema`) rather than as
 * an array of these objects: a 700-second flight sampled at 1 Hz has 700 frames
 * with 30 fields each, and repeating the key names 700 times inflates the payload
 * roughly fivefold for no benefit.
 */
export const TelemetrySampleSchema = z.object({
  timeSeconds: z.number(),
  positionMeters: Vec3Schema,
  velocityMetersPerSecond: Vec3Schema,
  altitudeMeters: z.number(),
  speedMetersPerSecond: z.number(),
  relativeSpeedMetersPerSecond: z.number(),
  massKg: z.number(),
  thrustNewtons: z.number(),
  throttle: z.number(),
  dragNewtons: z.number(),
  dynamicPressurePa: z.number(),
  mach: z.number(),
  densityKgPerCubicMeter: z.number(),
  accelerationMetersPerSecondSquared: z.number(),
  gLoad: z.number(),
  flightPathAngleDeg: z.number(),
  pitchDeg: z.number(),
  angleOfAttackDeg: z.number(),
  latitudeDeg: z.number(),
  longitudeDeg: z.number(),
  apoapsisAltitudeMeters: z.number(),
  periapsisAltitudeMeters: z.number(),
  inclinationDeg: z.number(),
  eccentricity: z.number(),
  stageIndex: z.number().int(),
  propellantRemainingKg: z.number(),
  phase: FlightPhaseSchema,
  deltaVExpendedMetersPerSecond: z.number(),
  dragLossMetersPerSecond: z.number(),
  gravityLossMetersPerSecond: z.number(),
});
export type TelemetrySampleDto = z.infer<typeof TelemetrySampleSchema>;

/** Columnar telemetry: one array per channel, all the same length. */
export const TelemetrySeriesSchema = z.object({
  length: z.number().int().nonnegative(),
  /**
   * JSON has no representation for NaN or ±Infinity. Open-conic apoapsides are
   * therefore encoded as null rather than being silently serialized to an
   * invalid value (or an indistinguishable zero).
   */
  channels: z.record(z.string(), z.array(z.number().nullable())),
  /** Phase per sample, as an index into `phaseNames`. */
  phaseIndices: z.array(z.number().int()),
  phaseNames: z.array(FlightPhaseSchema),
  /** Coordinate metadata required to interpret multi-body mission samples. */
  centralBodyIndices: z.array(z.number().int().nonnegative()).optional(),
  centralBodyNames: z.array(BodyIdSchema).optional(),
  referenceFrameIndices: z.array(z.number().int().nonnegative()).optional(),
  referenceFrameNames: z
    .array(
      z.enum([
        'earth-inertial',
        'earth-centered',
        'heliocentric',
        'body-centered',
      ]),
    )
    .optional(),
});
export type TelemetrySeriesDto = z.infer<typeof TelemetrySeriesSchema>;

export const FlightSummarySchema = z.object({
  durationSeconds: z.number(),
  maxAltitudeMeters: z.number(),
  maxSpeedMetersPerSecond: z.number(),
  maxDynamicPressurePa: z.number(),
  maxDynamicPressureTimeSeconds: z.number(),
  maxDynamicPressureAltitudeMeters: z.number(),
  maxGLoad: z.number(),
  maxGLoadTimeSeconds: z.number(),
  deltaVExpendedMetersPerSecond: z.number(),
  dragLossMetersPerSecond: z.number(),
  gravityLossMetersPerSecond: z.number(),
  reachedOrbit: z.boolean(),
  finalElements: OrbitalElementsSchema.nullable(),
  /** Null for an unbounded/open conic where that apsis does not exist. */
  finalApoapsisAltitudeMeters: z.number().nullable(),
  finalPeriapsisAltitudeMeters: z.number().nullable(),
  impacted: z.boolean(),
  propellantRemainingKg: z.number(),
  terminationReason: z.enum([
    'orbit',
    'mission-complete',
    'transfer-infeasible',
    'impact',
    'propellant-exhausted',
    'timeout',
    'step-limit',
  ]),
  steps: z.number().int(),
  reachedDestination: z.boolean().optional(),
  destinationBodyId: BodyIdSchema.optional(),
  transferDurationSeconds: z.number().nonnegative().optional(),
  transferDeltaVMetersPerSecond: z.number().nonnegative().optional(),
  capturedAtDestination: z.boolean().optional(),
  physicsModel: z.enum(['numerical-perturbed', 'kepler-reference']).optional(),
  forceModel: z.array(z.string()).optional(),
});
export type FlightSummaryDto = z.infer<typeof FlightSummarySchema>;

export const SimulationRunSchema = z.object({
  id: IdSchema,
  missionId: IdSchema.nullable(),
  status: z.enum(['queued', 'running', 'complete', 'failed', 'cancelled']),
  progress: z.number().min(0).max(1),
  request: SimulationRequestSchema,
  summary: FlightSummarySchema.nullable(),
  events: z.array(FlightEventSchema).default([]),
  error: z.string().nullable().default(null),
});
export type SimulationRunDto = z.infer<typeof SimulationRunSchema>;

export const SimulationRunRecordSchema = SimulationRunSchema.extend(AuditFieldsSchema.shape);
export type SimulationRunRecord = z.infer<typeof SimulationRunRecordSchema>;

// --- Analytics ---------------------------------------------------------------

export const RiskLevelSchema = z.enum(['low', 'moderate', 'elevated', 'high']);
export type RiskLevel = z.infer<typeof RiskLevelSchema>;

export const ConstraintViolationSchema = z.object({
  code: z.string(),
  severity: z.enum(['info', 'warning', 'error']),
  message: z.string(),
  /** Measured value that triggered the check, in the check's own units. */
  value: z.number().optional(),
  limit: z.number().optional(),
});
export type ConstraintViolationDto = z.infer<typeof ConstraintViolationSchema>;

export const AnalyticsSchema = z.object({
  /** Payload delivered per unit propellant burnt, dimensionless. */
  fuelEfficiency: z.number(),
  /** Estimated mission cost, USD. */
  estimatedCostUsd: z.number(),
  /** Cost per kilogram delivered, USD. */
  costPerKilogramUsd: z.number(),
  /** Estimated probability of mission success, 0-1. */
  launchSuccessProbability: z.number().min(0).max(1),
  /** Composite 0-100 score across efficiency, margin and risk. */
  performanceScore: z.number().min(0).max(100),
  /** Delta-V available beyond what the mission needs, metres per second. */
  deltaVMarginMetersPerSecond: z.number(),
  /** Payload as a fraction of gross lift-off mass. */
  payloadEfficiency: z.number(),
  riskLevel: RiskLevelSchema,
  riskFactors: z.array(z.string()),
  violations: z.array(ConstraintViolationSchema),
});
export type AnalyticsDto = z.infer<typeof AnalyticsSchema>;

// --- Optimizer ---------------------------------------------------------------

export const OptimizerObjectiveSchema = z.enum([
  'minimise-delta-v',
  'maximise-payload',
  'maximise-final-altitude',
  'minimise-max-q',
  'minimise-flight-time',
]);
export type OptimizerObjective = z.infer<typeof OptimizerObjectiveSchema>;

export const OptimizerVariableSchema = z.enum([
  'pitchStartAltitude',
  'turnEndAltitude',
  'turnExponent',
  'closedLoopAltitude',
  'verticalSpeedTimeConstant',
  'azimuth',
]);
export type OptimizerVariable = z.infer<typeof OptimizerVariableSchema>;

export const OptimizerRequestSchema = z.object({
  missionId: IdSchema.optional(),
  objective: OptimizerObjectiveSchema,
  variables: z.array(OptimizerVariableSchema).min(1),
  /** Algorithm. Differential evolution explores; Nelder-Mead refines. */
  algorithm: z.enum(['differential-evolution', 'nelder-mead', 'both']).default('both'),
  /** Population size for differential evolution. */
  populationSize: z.number().int().min(4).max(200).default(24),
  /** Maximum objective evaluations. */
  maxEvaluations: z.number().int().min(10).max(20_000).default(1200),
  /** Deterministic seed, so a run can be reproduced exactly. */
  seed: z.number().int().default(1),
  settings: SimulationSettingsSchema.default(DEFAULT_SIMULATION_SETTINGS),
});
export type OptimizerRequest = z.infer<typeof OptimizerRequestSchema>;

export const OptimizerResultSchema = z.object({
  id: IdSchema,
  status: z.enum(['queued', 'running', 'complete', 'failed', 'cancelled']),
  progress: z.number().min(0).max(1),
  objective: OptimizerObjectiveSchema,
  /** Best objective value found. Lower is better; maximisation is negated internally. */
  bestValue: z.number().nullable(),
  /** Best parameter set found, keyed by variable name. */
  bestParameters: z.record(z.string(), z.number()).nullable(),
  /** Objective value per evaluation, for the convergence plot. */
  convergence: z.array(z.number()).default([]),
  evaluations: z.number().int().nonnegative(),
  /** Ascent programme built from the best parameters, ready to apply. */
  resultingProgram: AscentProgramSchema.nullable(),
  error: z.string().nullable().default(null),
});
export type OptimizerResultDto = z.infer<typeof OptimizerResultSchema>;
